import { isRouteErrorResponse, useRouteError } from 'react-router';
import { Compass, RotateCw, TriangleAlert } from 'lucide-react';
import { Button, ButtonLink, EmptyState } from '../ui';
import { AuthFrame } from '../auth/AuthFrame';
import { isChunkLoadError } from '../lib/chunks';

export function NotFoundPage({ inShell }: { inShell?: boolean }) {
  const body = (
    <EmptyState
      icon={<Compass />}
      title="Nothing lives at this address"
      actions={
        <ButtonLink to="/" variant="secondary">
          Go home
        </ButtonLink>
      }
    >
      The link may be mistyped, or the page may have moved.
    </EmptyState>
  );
  if (inShell) return body;
  return (
    <AuthFrame>
      <div className="auth-card">{body}</div>
    </AuthFrame>
  );
}

/** Last-resort boundary for render errors, so a bug never shows a blank page. */
export function RouteError() {
  const err = useRouteError();
  if (isRouteErrorResponse(err) && err.status === 404) return <NotFoundPage />;
  if (isChunkLoadError(err)) {
    // Usually a tab left open across a deploy; the same URL loads fine once reloaded.
    return (
      <AuthFrame>
        <div className="auth-card">
          <EmptyState
            icon={<RotateCw />}
            title="CertiHash has been updated"
            actions={
              <Button variant="primary" icon={<RotateCw />} onClick={() => window.location.reload()}>
                Reload
              </Button>
            }
          >
            Part of this page couldn’t be loaded, most likely because a newer version was released while it was open. Reload to continue.
          </EmptyState>
        </div>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame>
      <div className="auth-card">
        <EmptyState
          icon={<TriangleAlert />}
          tone="error"
          title="Something went wrong"
          actions={
            <ButtonLink to="/" variant="secondary" reloadDocument>
              Reload CertiHash
            </ButtonLink>
          }
        >
          This page hit an unexpected error. Reloading usually fixes it; if it keeps happening, let us know what you were doing.
        </EmptyState>
      </div>
    </AuthFrame>
  );
}
